import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { companiesApi } from '../store/services/companies';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  code: yup.string().required('Code is required'),
  address: yup.string().required('Address is required'),
  contact_number: yup.string().required('Contact number is required'),
});

export default function Companies() {
  const [open, setOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  // Get the current user from Redux state
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  // Check if user has permission to add/edit/delete
  const hasAdminPermission = currentUser?.role === 'SuperAdmin';

  const { data: companies, isLoading } = companiesApi.useGetCompaniesQuery();
  const [createCompany] = companiesApi.useCreateCompanyMutation();
  const [updateCompany] = companiesApi.useUpdateCompanyMutation();
  const [deleteCompany] = companiesApi.useDeleteCompanyMutation();

  const formik = useFormik({
    initialValues: {
      name: '',
      code: '',
      address: '',
      contact_number: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedCompany) {
          await updateCompany({
            id: selectedCompany._id,
            data: values,
          }).unwrap();
        } else {
          await createCompany(values).unwrap();
        }
        handleClose();
      } catch (error) {
        console.error('Failed to save company:', error);
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedCompany(null);
    formik.resetForm();
    setOpen(true);
  };

  const handleEdit = (company: any) => {
    setSelectedCompany(company);
    formik.setValues({
      name: company.name,
      code: company.code,
      address: company.address,
      contact_number: company.contact_number,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await deleteCompany(id).unwrap();
      } catch (error) {
        console.error('Failed to delete company:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Companies
        </Typography>
        {hasAdminPermission && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleClickOpen}
          >
            Add Company
          </Button>
        )}
      </Box>
      
      {!hasAdminPermission && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to companies.
        </Alert>
      )}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Contact Number</TableCell>
              {hasAdminPermission && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {companies?.map((company) => (
              <TableRow key={company._id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.code}</TableCell>
                <TableCell>{company.address}</TableCell>
                <TableCell>{company.contact_number}</TableCell>
                {hasAdminPermission && (
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(company)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(company._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{selectedCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              margin="dense"
              id="code"
              name="code"
              label="Code"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
            />
            <TextField
              fullWidth
              margin="dense"
              id="address"
              name="address"
              label="Address"
              multiline
              rows={3}
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
            <TextField
              fullWidth
              margin="dense"
              id="contact_number"
              name="contact_number"
              label="Contact Number"
              value={formik.values.contact_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
              helperText={formik.touched.contact_number && formik.errors.contact_number}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedCompany ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}